import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { confirmSignUp, resendSignUpCode, confirmSignIn } from 'aws-amplify/auth';
import './mfaVerification.css';
import GlueLogo from '../glueLogo';

const MfaVerification = () => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Determine flow
  const user = location.state?.user; // Present only in login + MFA case
  const email = location.state?.email;
  const password = location.state?.password;

  const isMfaFlow = Boolean(user); // true = MFA, false = sign-up confirmation

  useEffect(() => {
    let timer;
    if (cooldown > 0) {
      timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleVerify = async () => {
    if (!code || code.length !== 6 || isNaN(code)) {
      setError('Please enter a valid 6-digit code.');
      return;
    }

    setError('');
    setIsVerifying(true);

    try {
      if (isMfaFlow) {
        // 👉 Handle MFA
        await confirmSignIn(user, code);
        navigate('/glue-dashboard');
      } else {
        // 👉 Handle Sign-up Email Confirmation
        await confirmSignUp({ username: email, confirmationCode: code });

        // Auto-login after confirmation
        const { signIn } = await import('aws-amplify/auth');
        const signedInUser = await signIn({ username: email, password });

        if (
          signedInUser.challengeName === 'SMS_MFA' ||
          signedInUser.challengeName === 'SOFTWARE_TOKEN_MFA'
        ) {
          navigate('/mfa', { state: { user: signedInUser, email, password } }); // optional MFA step
        } else {
          navigate('/glue-dashboard');
        }
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.message || 'Invalid verification code.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (isMfaFlow) {
      setError("MFA code resend is not supported from frontend.");
      return;
    }

    setError('');
    setIsResending(true);
    try {
      await resendSignUpCode({ username: email });
      setCooldown(30);
      alert('Verification code resent to your email.');
    } catch (err) {
      console.error('Error resending code:', err);
      setError(err.message || 'Failed to resend verification code.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="mfa-container">
      <div className="mfa-box">
        <h2 className="mfa-subtitle">
          <Link to="/" style={{ textDecoration: 'none' }}><GlueLogo height="50" /></Link>
        </h2>
        <h1 className="mfa-title">{isMfaFlow ? 'MFA Verification' : 'Email Verification'}</h1>
        <p className="mfa-desc">
          {isMfaFlow
            ? 'Enter the 6-digit MFA code from your authenticator or SMS.'
            : 'Enter the 6-digit verification code sent to your email address.'}
        </p>

        <input
          type="text"
          maxLength={6}
          inputMode="numeric"
          placeholder="Verification code"
          className="mfa-input"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />

        {error && <div className="mfa-error">{error}</div>}

        {!isMfaFlow && (
          <button
            className="mfa-resend"
            onClick={handleResend}
            disabled={cooldown > 0 || isResending}
          >
            {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
          </button>
        )}

        <button className="mfa-button" onClick={handleVerify} disabled={isVerifying}>
          {isVerifying ? 'Verifying...' : 'Verify'}
        </button>
      </div>
    </div>
  );
};

export default MfaVerification;
